import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, checkGardenerProfileExists } from '../lib/supabase';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import Logo from '../components/Logo';

export default function MagicLinkCallback() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const handleMagicLinkAuth = async () => {
      try {
        setLoading(true);
        
        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;
        
        if (!session) {
          setError('Invalid or expired magic link. Please request a new one.');
          setLoading(false);
          return;
        }
        
        // 1. Try to find a pending submission for this user's email
        const { data: submission, error: submissionFetchError } = await supabase
          .from('grow_box_submissions')
          .select('*')
          .eq('email', session.user.email)
          .eq('profile_updated', false)
          .order('submitted_at', { ascending: false })
          .maybeSingle();

        if (submissionFetchError) {
          console.error('Error fetching landing page submission:', submissionFetchError);
          // Continue execution, don't block auth flow
        }

        // 2. Check if gardener profile exists
        const hasProfile = await checkGardenerProfileExists(session.user.id);

        // Prepare profile data from submission (if available)
        const profileUpdates: Record<string, any> = {
          level: 'beginner',
          garden_type: ['containers'],
          grow_vegetables: true,
          onboarding_completed: false
        };

        if (submission) {
          // Map submission data to gardener_profiles fields
          if (submission.first_name) profileUpdates.first_name = submission.first_name;
          if (submission.zip_code) profileUpdates.zip_code = submission.zip_code;
          if (submission.box_theme) profileUpdates.fall_box_theme = submission.box_theme;

          // Map sun_exposure from grow_box_submissions to gardener_profiles' sunlight_hours
          if (submission.sun_exposure) {
            if (submission.sun_exposure === 'full_sun') profileUpdates.sunlight_hours = 'full_sun';
            else if (submission.sun_exposure === 'part_sun') profileUpdates.sunlight_hours = 'part_sun_part_shade';
            else if (submission.sun_exposure === 'shade') profileUpdates.sunlight_hours = 'full_shade';
          }

          // Map garden_setup from grow_box_submissions to gardener_profiles' growing_space_type
          if (submission.garden_setup) {
            if (submission.garden_setup === 'in_ground_raised') profileUpdates.growing_space_type = 'raised_bed';
            else if (submission.garden_setup === 'containers_bags') profileUpdates.growing_space_type = 'containers';
            else if (submission.garden_setup === 'no_setup') profileUpdates.growing_space_type = 'none_yet';
          }

          // Map growing_environment from grow_box_submissions (indoor/outdoor)
          if (submission.growing_environment) {
            // Store growing environment in profile if needed
            // This could be used to determine whether user is indoor/outdoor gardening
            profileUpdates.growing_environment = submission.growing_environment;
          }

          if (submission.phone_number) profileUpdates.phone_number = submission.phone_number;
          
          // Mark onboarding as completed if we have comprehensive data from /grow page
          if (submission.source_page === 'grow' && submission.first_name && submission.zip_code) {
            profileUpdates.onboarding_completed = true;
          }
        }

        if (hasProfile) {
          const { error: updateError } = await supabase
            .from('gardener_profiles')
            .update(profileUpdates)
            .eq('id', session.user.id);

          if (updateError) {
            console.error('Error updating gardener profile:', updateError);
          }
        } else {
          const { error: insertError } = await supabase
            .from('gardener_profiles')
            .insert([{ id: session.user.id, ...profileUpdates }]);

          if (insertError) {
            console.error('Error creating gardener profile:', insertError);
          }
        }

        // 3. Update the submission record to mark it as processed
        if (submission) {
          const { error: updateSubmissionError } = await supabase
            .from('grow_box_submissions')
            .update({ user_id: session.user.id, profile_updated: true })
            .eq('id', submission.id);

          if (updateSubmissionError) {
            console.error('Error marking submission as processed:', updateSubmissionError);
          }
        }
        
        setSuccess(true);
        setTimeout(() => {
          navigate('/my-garden', {
            state: {
              showOnboarding: !hasProfile,
              message: !hasProfile ? 'Complete your garden profile to get personalized recommendations!' : undefined
            }
          });
        }, 2000);
      } catch (err) {
        console.error('Error handling magic link auth:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    handleMagicLinkAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
        <div className="flex justify-center mb-6">
          <Logo size="lg" showText={false} variant="full" />
        </div>
        
        {loading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-12 w-12 text-primary-500 animate-spin mb-4" />
            <h2 className="text-xl font-semibold mb-2">Signing you in...</h2>
            <p className="text-gray-600">Please wait while we verify your magic link.</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Authentication Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/auth')}
              className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
            >
              Go to Sign In
            </button>
          </div>
        ) : success ? (
          <div className="flex flex-col items-center">
            <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Successfully Signed In!</h2>
            <p className="text-gray-600">Redirecting you to your garden...</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}